import { useSearchParams } from "react-router-dom";
import PageHero from "../components/PageHero";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const SECTIONS = {
  disclosures: {
    label: "Legal Disclosures",
    content: [
      { h: "Government affiliation", p: "CF Full Circle is not affiliated with USCIS, the U.S. Department of Justice, the Department of State, the Internal Revenue Service, or any other government agency. Government forms are available for free from the relevant agencies." },
      { h: "Authorized representation", p: "Immigration legal representation is offered only through appropriately authorized attorneys or DOJ Accredited Representatives acting through their recognized organization and within their authorized scope. Representation before immigration courts and the Board of Immigration Appeals is offered only when provided by a fully accredited representative or authorized attorney and within the organization's approved scope." },
      { h: "No guaranteed outcomes", p: "Government agencies make all decisions regarding immigration benefits, passports, visas, vital records, identification documents, tax identification numbers, and other government-issued documents. CF Full Circle does not and cannot guarantee approvals, processing times, government issuance of records, visas, passports, identification, tax outcomes, or third-party results." },
      { h: "Third-party licensed services", p: "Services requiring separately licensed or certified professionals — including laboratory testing, tax preparation, insurance, financial services, and medical services — may be provided through qualified third-party providers. Laboratory analyses are performed by certified laboratories; blood testing is performed only through appropriately licensed personnel and providers." },
      { h: "Acceptance Agent status", p: "IRS Acceptance Agent or Certifying Acceptance Agent services are offered only if and when CF Full Circle has received official IRS authorization. Nothing on this website should be read as claiming such authorization unless explicitly stated." },
    ],
  },
  privacy: {
    label: "Privacy Policy",
    content: [
      { h: "What we collect", p: "We collect the information you provide through intake forms, contact forms, your client portal, and documents you upload — including identity information, immigration history, and contact details." },
      { h: "How we use it", p: "Your information is used to evaluate, prepare, coordinate, and manage the services you request, and to communicate with you about your journey. We do not sell your information." },
      { h: "How we protect it", p: "Immigration documents are treated as highly sensitive. Documents are stored in encrypted object storage and served only through authenticated portal access — never through public links. Access is role-based: only team members working on your journey can open your file. Sessions expire automatically, and staff access is logged." },
      { h: "Notifications", p: "Email notifications never include sensitive case details or document contents. They simply let you know something new is waiting for you in your secure portal." },
      { h: "Retention and deletion", p: "You may request deletion of your documents and data, subject to legal record-keeping obligations. Contact us at fciicenter@gmail.com." },
    ],
  },
  terms: {
    label: "Terms of Use",
    content: [
      { h: "Acceptance", p: "By using this website you agree to these terms. If you do not agree, please do not use the site." },
      { h: "No attorney-client relationship from website use", p: "Using this website, reading resources, or submitting a contact or intake form does not by itself create an attorney-client or representative-client relationship. That relationship begins only after a signed engagement agreement." },
      { h: "General information only", p: "Website content and resources are general information, not legal, tax, medical, or financial advice for your specific situation." },
      { h: "Acceptable use", p: "Do not upload unlawful content, attempt to access accounts that are not yours, or disrupt the service." },
    ],
  },
  accessibility: {
    label: "Accessibility",
    content: [
      { h: "Our commitment", p: "CF Full Circle is committed to a website that everyone can use, including people using screen readers, keyboard navigation, or other assistive technology." },
      { h: "Standards", p: "We design for high color contrast, readable font sizes, keyboard navigation, image alt text, properly labeled forms, and clear error explanations." },
      { h: "Need help?", p: "If any part of this website is difficult for you to use, contact us at fciicenter@gmail.com or (833) 477-7587 and we will assist you directly." },
    ],
  },
  rights: {
    label: "Client Rights",
    content: [
      { h: "You have the right to", p: "Clear explanations in plain language; honest information about what we can and cannot do; confidentiality of your documents and case information; copies of filings prepared on your behalf; an itemized statement of fees that separates professional fees from government and third-party fees; and to end services at any time." },
      { h: "You have the right to know", p: "Who is working on your matter and their role; when a matter requires a differently licensed professional; and the status of your case at any time through your client portal." },
      { h: "Concerns", p: "If you believe your rights have not been respected, contact us directly. You may also contact the relevant state bar or the DOJ Executive Office for Immigration Review regarding recognized organizations and accredited representatives." },
    ],
  },
  emergency: {
    label: "Emergency Disclaimer",
    content: [
      { h: "This website is not for emergencies", p: "CF Full Circle is not an emergency service and does not monitor messages around the clock. If you or someone else is in immediate danger, call 911." },
      { h: "Detention emergencies", p: "If a family member has been detained, call us during business hours and say so immediately — detained matters are triaged first. Outside business hours, contact a licensed immigration attorney or accredited representative who handles emergency detained cases." },
      { h: "Crisis support", p: "For mental health crises, call or text 988 (Suicide & Crisis Lifeline). For domestic violence, call the National Domestic Violence Hotline at 1-800-799-7233." },
    ],
  },
  careers: {
    label: "Careers",
    content: [
      { h: "Work with us", p: "We are building a national model for immigrant legal access and integration. Roles include case managers, newcomer navigators, document specialists, and client support." },
      { h: "Apply", p: "Send your résumé and a short note about why this work matters to you to fciicenter@gmail.com with the subject line 'Careers'." },
    ],
  },
  partner: {
    label: "Partner With Us",
    content: [
      { h: "Referral partners", p: "We work with attorneys, nonprofits, employers, schools, faith communities, and government programs that serve immigrants and newcomers." },
      { h: "Get in touch", p: "To discuss partnership or referral arrangements, email fciicenter@gmail.com with the subject line 'Partnership' or use our contact form." },
    ],
  },
};

export default function Legal() {
  const [params] = useSearchParams();
  const tab = params.get("tab");
  const defaultTab = SECTIONS[tab] ? tab : "disclosures";

  return (
    <div data-testid="legal-page">
      <PageHero
        eyebrow="Legal"
        title="Clear words, honest boundaries"
        lead="We believe trust starts with transparency. These pages explain exactly what CF Full Circle is, what we do, and the boundaries we respect."
      />
      <section className="py-12 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue={defaultTab} key={defaultTab}>
            <TabsList className="flex flex-wrap h-auto gap-1 bg-sand p-1.5 rounded-xl" data-testid="legal-tabs">
              {Object.entries(SECTIONS).map(([key, s]) => (
                <TabsTrigger key={key} value={key} className="text-xs sm:text-sm data-[state=active]:bg-surface data-[state=active]:text-navy rounded-lg" data-testid={`legal-tab-${key}`}>
                  {s.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {Object.entries(SECTIONS).map(([key, s]) => (
              <TabsContent key={key} value={key} className="mt-8 space-y-8" data-testid={`legal-content-${key}`}>
                <h2 className="text-2xl font-serif font-semibold text-navy">{s.label}</h2>
                {s.content.map((c) => (
                  <div key={c.h}>
                    <h3 className="text-base font-sans font-semibold text-navy mb-2">{c.h}</h3>
                    <p className="text-sm text-mutedink leading-relaxed">{c.p}</p>
                  </div>
                ))}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
    </div>
  );
}
